// RegularAttendanceView.jsx
// Props: className ("Class 1"), section ("A"), group ("Bio Maths" | null)

import { useState, useEffect, useCallback, useMemo } from "react";
import { ArrowRight } from "lucide-react";
const API_BASE = process.env.REACT_APP_API_URL || "https://schoolcrm-fzcv.onrender.com";

// ── math helpers ──────────────────────────────────────────────────────────────
function pct(num, den) {
  if (!den) return 0;
  return Math.round(num / den * 100);
}
function attColor(p) {
  if (p >= 75) return "#10b981";
  if (p >= 50) return "#f59e0b";
  return "#ef4444";
}

// ── Bar ───────────────────────────────────────────────────────────────────────
function Bar({
  value,
  color,
  height = 5
}) {
  return <div style={{
    height,
    borderRadius: 6,
    background: "#e2e8f0",
    overflow: "hidden"
  }}>
      <div style={{
      width: `${Math.max(0, Math.min(100, value))}%`,
      height: "100%",
      background: color,
      borderRadius: 6,
      transition: "width .4s"
    }} />
    </div>;
}

// ── date helpers ──────────────────────────────────────────────────────────────
function todayIST() {
  return new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);
}
function getMondayOf(d) {
  const dt = new Date(d + "T00:00:00");
  const day = dt.getDay();
  dt.setDate(dt.getDate() + (day === 0 ? -6 : 1 - day));
  return dt.toISOString().slice(0, 10);
}
function addDays(d, n) {
  const dt = new Date(d + "T00:00:00");
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
}
function lastDOM(y, m) {
  return new Date(+y, +m, 0).toISOString().slice(0, 10);
}
function fmtMonth(ym) {
  const [y, m] = ym.split("-");
  return new Date(+y, +m - 1, 1).toLocaleString("en-IN", {
    month: "long",
    year: "numeric"
  });
}
function fmtDate(s) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
function termRange(t, y) {
  const n = +y;
  return {
    T1: {
      from: `${n}-06-01`,
      to: `${n}-09-30`
    },
    T2: {
      from: `${n}-10-01`,
      to: `${n + 1}-01-31`
    },
    T3: {
      from: `${n + 1}-02-01`,
      to: `${n + 1}-05-31`
    }
  }[t] || {
    from: `${n}-06-01`,
    to: `${n}-09-30`
  };
}
function getRange(mode, sd, ws, mo, yr, tm) {
  if (mode === "single") return {
    from: sd,
    to: sd
  };
  if (mode === "week") return {
    from: ws,
    to: addDays(ws, 6)
  };
  if (mode === "month") {
    const [y, m] = mo.split("-");
    return {
      from: `${y}-${m}-01`,
      to: lastDOM(y, m)
    };
  }
  if (mode === "year") return {
    from: `${yr}-01-01`,
    to: `${yr}-12-31`
  };
  if (mode === "term") return termRange(tm, yr);
  return {
    from: sd,
    to: sd
  };
}

// ── picker style ──────────────────────────────────────────────────────────────
const PS = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  color: "#1e293b",
  padding: "8px 14px",
  fontSize: 13,
  fontFamily: "'Poppins', sans-serif",
  outline: "none"
};

// ── subject color palette (white background + accent border/dot) ──────────────
const SUBJ_COLORS = [{
  border: "#c7d2fe",
  text: "#1e40af",
  dot: "#3b82f6"
}, {
  border: "#99f6e4",
  text: "#0f766e",
  dot: "#14b8a6"
}, {
  border: "#fde68a",
  text: "#92400e",
  dot: "#f59e0b"
}, {
  border: "#fecaca",
  text: "#991b1b",
  dot: "#ef4444"
}, {
  border: "#e9d5ff",
  text: "#6b21a5",
  dot: "#a855f7"
}, {
  border: "#bbf7d0",
  text: "#166534",
  dot: "#22c55e"
}, {
  border: "#fed7aa",
  text: "#9a3412",
  dot: "#f97316"
}, {
  border: "#fbcfe8",
  text: "#9d174d",
  dot: "#ec4899"
}];
function subjColor(i) {
  return SUBJ_COLORS[i % SUBJ_COLORS.length];
}

// ─────────────────────────────────────────────────────────────────────────────
// StudentModal — shows overall + subject-wise breakdown (white background)
// ─────────────────────────────────────────────────────────────────────────────
function StudentModal({
  student,
  flatRecords,
  timetableSubjects,
  onClose
}) {
  const [activeSubject, setActiveSubject] = useState(null);
  const stuId = String(student._id);
  const allRecs = flatRecords.filter(r => String(r.studentId) === stuId);
  const totalPresent = allRecs.filter(r => r.status === "P").length;
  const totalAbsent = allRecs.filter(r => r.status === "A").length;
  const totalLate = allRecs.filter(r => r.status === "L").length;
  const totalPeriods = allRecs.length;
  const overallPct = pct(totalPresent + totalLate, totalPeriods);
  const subjRecs = activeSubject ? allRecs.filter(r => r.subject === activeSubject) : allRecs;
  const sPresent = subjRecs.filter(r => r.status === "P").length;
  const sAbsent = subjRecs.filter(r => r.status === "A").length;
  const sLate = subjRecs.filter(r => r.status === "L").length;
  const sPeriods = subjRecs.length;
  const sPct = pct(sPresent + sLate, sPeriods);
  const sColor = attColor(sPct);
  const subjectList = useMemo(() => {
    const fromTT = timetableSubjects.map(s => s.subject);
    const fromRec = [...new Set(allRecs.map(r => r.subject).filter(Boolean))];
    const all = [...new Set([...fromTT, ...fromRec])];
    return all.map(subj => {
      const ttEntry = timetableSubjects.find(s => s.subject === subj);
      const teacher = ttEntry?.teacher || "";
      const recs = allRecs.filter(r => r.subject === subj);
      const p = recs.filter(r => r.status === "P").length;
      const a = recs.filter(r => r.status === "A").length;
      const l = recs.filter(r => r.status === "L").length;
      const tot = recs.length;
      const pc = pct(p + l, tot);
      return {
        subj,
        teacher,
        present: p,
        absent: a,
        late: l,
        total: tot,
        pct: pc
      };
    });
  }, [allRecs, timetableSubjects]);
  const displayRecs = [...subjRecs].sort((a, b) => new Date(b.date) - new Date(a.date));
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 16
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      background: "#fff",
      borderRadius: 24,
      width: "100%",
      maxWidth: 680,
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
      color: "#1e293b",
      fontFamily: "'Poppins', sans-serif",
      overflow: "hidden",
      boxShadow: "0 24px 48px rgba(0,0,0,0.15)"
    }}>
        {/* Header */}
        <div style={{
        padding: "22px 26px 0",
        flexShrink: 0
      }}>
          <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20
        }}>
            <div style={{
            display: "flex",
            gap: 14,
            alignItems: "center"
          }}>
              <div style={{
              width: 52,
              height: 52,
              borderRadius: 16,
              background: "#eef2ff",
              color: "#3b82f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 700,
              flexShrink: 0
            }}>
                <i className="fas fa-user-graduate"></i>
              </div>
              <div>
                <div style={{
                fontWeight: 700,
                fontSize: 20,
                color: "#0f172a"
              }}>{student.studentName}</div>
                <div style={{
                fontSize: 12,
                color: "#64748b",
                marginTop: 2
              }}>Roll #{student.rollNo} &nbsp;·&nbsp; {student.gender || ""}</div>
              </div>
            </div>
            <button onClick={onClose} style={{
            background: "#f1f5f9",
            border: "none",
            borderRadius: 10,
            color: "#64748b",
            fontSize: 16,
            cursor: "pointer",
            width: 34,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}><i className="fas fa-times"></i></button>
          </div>

          {/* Overall stats */}
          <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(4,1fr)",
          gap: 10,
          marginBottom: 18
        }}>
            {[{
            label: "Present",
            val: totalPresent,
            color: "#10b981"
          }, {
            label: "Absent",
            val: totalAbsent,
            color: "#ef4444"
          }, {
            label: "Late",
            val: totalLate,
            color: "#f59e0b"
          }, {
            label: "Overall",
            val: totalPeriods > 0 ? `${overallPct}%` : "—",
            color: attColor(overallPct)
          }].map(c => <div key={c.label} style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 14,
            padding: "12px 10px",
            textAlign: "center"
          }}>
                <div style={{
              fontSize: 22,
              fontWeight: 700,
              color: c.color
            }}>{c.val}</div>
                <div style={{
              fontSize: 10,
              color: "#64748b",
              marginTop: 2
            }}>{c.label}</div>
              </div>)}
          </div>

          {/* Subject tabs */}
          <div style={{
          marginBottom: 16
        }}>
            <div style={{
            fontSize: 11,
            color: "#64748b",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            marginBottom: 10
          }}>
              Subjects — click to filter
            </div>
            <div style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap"
          }}>
              <button onClick={() => setActiveSubject(null)} style={{
              padding: "8px 16px",
              borderRadius: 30,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'Poppins', sans-serif",
              transition: "all .15s",
              background: activeSubject === null ? "#3b82f6" : "#fff",
              border: activeSubject === null ? "none" : "1px solid #cbd5e1",
              color: activeSubject === null ? "#fff" : "#475569"
            }}>
                <i className="fas fa-list"></i> All Subjects
              </button>

              {subjectList.map((s, i) => {
              const c = subjColor(i);
              const isActive = activeSubject === s.subj;
              return <button key={s.subj} onClick={() => setActiveSubject(isActive ? null : s.subj)} style={{
                padding: "8px 16px",
                borderRadius: 30,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Poppins', sans-serif",
                transition: "all .15s",
                background: isActive ? c.dot : "#fff",
                border: `1px solid ${c.border}`,
                color: isActive ? "#fff" : c.text,
                display: "flex",
                alignItems: "center",
                gap: 8
              }}>
                    <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: isActive ? "#fff" : c.dot,
                  flexShrink: 0
                }} />
                    <span>{s.subj}</span>
                    {s.teacher && <span style={{
                  opacity: 0.7,
                  fontWeight: 400,
                  marginLeft: 4
                }}>· {s.teacher}</span>}
                    {s.total > 0 && <span style={{
                  marginLeft: 6,
                  background: "rgba(0,0,0,0.08)",
                  borderRadius: 20,
                  padding: "2px 8px",
                  fontSize: 10
                }}>
                        {s.pct}%
                      </span>}
                  </button>;
            })}
            </div>
          </div>

          {/* Selected subject stats */}
          {activeSubject && <div style={{
          background: "#f8fafc",
          borderRadius: 16,
          padding: "14px 18px",
          marginBottom: 16
        }}>
              <div style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#0f172a",
            marginBottom: 10
          }}>
                {activeSubject}
                {subjectList.find(s => s.subj === activeSubject)?.teacher && <span style={{
              fontSize: 11,
              color: "#64748b",
              fontWeight: 400,
              marginLeft: 8
            }}>
                    · {subjectList.find(s => s.subj === activeSubject)?.teacher}
                  </span>}
              </div>
              <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 10,
            marginBottom: 12
          }}>
                {[{
              label: "Present",
              val: sPresent,
              color: "#10b981"
            }, {
              label: "Absent",
              val: sAbsent,
              color: "#ef4444"
            }, {
              label: "Late",
              val: sLate,
              color: "#f59e0b"
            }, {
              label: "Periods",
              val: sPeriods,
              color: "#3b82f6"
            }].map(c => <div key={c.label} style={{
              textAlign: "center"
            }}>
                    <div style={{
                fontSize: 20,
                fontWeight: 700,
                color: c.color
              }}>{c.val}</div>
                    <div style={{
                fontSize: 10,
                color: "#64748b",
                marginTop: 2
              }}>{c.label}</div>
                  </div>)}
              </div>
              <div style={{
            display: "flex",
            alignItems: "center",
            gap: 10
          }}>
                <div style={{
              flex: 1
            }}><Bar value={sPct} color={sColor} /></div>
                <span style={{
              fontSize: 13,
              fontWeight: 700,
              color: sColor,
              minWidth: 40
            }}>{sPeriods > 0 ? `${sPct}%` : "—"}</span>
              </div>
              {sPeriods > 0 && sPct < 75 && <div style={{
            fontSize: 11,
            color: "#ef4444",
            marginTop: 8
          }}><i className="fas fa-exclamation-triangle"></i> Below 75% in this subject</div>}
            </div>}
        </div>

        {/* Records list */}
        <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "0 26px 22px"
      }}>
          <div style={{
          fontSize: 11,
          color: "#64748b",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          marginBottom: 10
        }}>
            {activeSubject ? `${activeSubject} Records` : "All Period Records"} ({displayRecs.length})
          </div>

          {displayRecs.length === 0 ? <div style={{
          textAlign: "center",
          padding: "28px 0",
          color: "#94a3b8",
          fontSize: 13
        }}>
              No records found{activeSubject ? ` for ${activeSubject}` : ""}.
            </div> : <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 8
        }}>
              {displayRecs.map((r, i) => {
            const sc = r.status === "P" ? "#10b981" : r.status === "A" ? "#ef4444" : "#f59e0b";
            const sLabel = r.status === "P" ? "Present" : r.status === "A" ? "Absent" : "Late";
            const si = subjectList.findIndex(s => s.subj === r.subject);
            const sc2 = si >= 0 ? subjColor(si) : SUBJ_COLORS[0];
            return <div key={i} style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#f8fafc",
              borderRadius: 12,
              padding: "10px 16px",
              border: "1px solid #e2e8f0"
            }}>
                    <div style={{
                flex: 1,
                minWidth: 0
              }}>
                      <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4
                }}>
                        <span style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#0f172a"
                  }}>{r.periodLabel || `Period ${r.periodId}`}</span>
                        {r.subject && <span style={{
                    background: "#fff",
                    color: sc2.text,
                    border: `1px solid ${sc2.border}`,
                    borderRadius: 20,
                    fontSize: 10,
                    fontWeight: 600,
                    padding: "2px 10px"
                  }}>
                            {r.subject}
                          </span>}
                      </div>
                      <div style={{
                  fontSize: 10,
                  color: "#64748b"
                }}>
                        {fmtDate(r.date)}
                        {r.timeRange ? ` · ${r.timeRange}` : ""}
                        {r.teacher ? ` · ${r.teacher}` : ""}
                      </div>
                    </div>
                    <span style={{
                background: `${sc}10`,
                color: sc,
                border: `1px solid ${sc}30`,
                borderRadius: 30,
                padding: "4px 14px",
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
                marginLeft: 12
              }}>
                      {sLabel}
                    </span>
                  </div>;
          })}
            </div>}
        </div>
      </div>
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export default function RegularAttendanceView({
  className,
  section,
  group
}) {
  const classKey = useMemo(() => {
    if (!className || !section) return null;
    return group ? `${className} ${group}-${section}` : `${className}-${section}`;
  }, [className, section, group]);
  const dbClassName = useMemo(() => {
    if (!className) return null;
    return group ? `${className} ${group}` : className;
  }, [className, group]);
  const today = todayIST();
  const [viewMode, setViewMode] = useState("month");
  const [singleDate, setSingleDate] = useState(today);
  const [weekStart, setWeekStart] = useState(getMondayOf(today));
  const [month, setMonth] = useState(today.slice(0, 7));
  const [year, setYear] = useState(today.slice(0, 4));
  const [term, setTerm] = useState("T1");
  const [students, setStudents] = useState([]);
  const [flatRecords, setFlatRecords] = useState([]);
  const [timetableSubjects, setTimetableSubjects] = useState([]);
  const [loadingStu, setLoadingStu] = useState(true);
  const [loadingAtt, setLoadingAtt] = useState(false);
  const [stuError, setStuError] = useState("");
  const [attError, setAttError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("roll");
  const [modalStu, setModalStu] = useState(null);

  // Fetch timetable
  useEffect(() => {
    if (!classKey) return;
    fetch(`${API_BASE}/api/timetables/${encodeURIComponent(classKey)}`).then(r => r.ok ? r.json() : null).then(data => {
      if (!data?.timetable) return;
      const seen = new Map();
      Object.values(data.timetable).forEach(dayObj => {
        Object.values(dayObj || {}).forEach(cell => {
          if (cell?.subject && !seen.has(cell.subject)) {
            seen.set(cell.subject, cell.teacher || "");
          }
        });
      });
      setTimetableSubjects([...seen.entries()].map(([subject, teacher]) => ({
        subject,
        teacher
      })));
    }).catch(() => setTimetableSubjects([]));
  }, [classKey]);

  // Fetch students
  const fetchStudents = useCallback(async () => {
    if (!classKey) return;
    setLoadingStu(true);
    setStuError("");
    try {
      const res = await fetch(`${API_BASE}/api/admissions/by-class/${encodeURIComponent(classKey)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStudents(Array.isArray(data) ? data.sort((a, b) => (a.rollNo || 0) - (b.rollNo || 0)) : []);
    } catch (e) {
      setStuError(e.message);
      setStudents([]);
    } finally {
      setLoadingStu(false);
    }
  }, [classKey]);
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Fetch attendance
  const fetchAttendance = useCallback(async () => {
    if (!dbClassName || !section) return;
    setLoadingAtt(true);
    setAttError("");
    try {
      const {
        from,
        to
      } = getRange(viewMode, singleDate, weekStart, month, year, term);
      const params = new URLSearchParams({
        className: dbClassName,
        section,
        startDate: from,
        endDate: to
      });
      if (group && group !== "null") params.set("group", group);
      const res = await fetch(`${API_BASE}/api/attendance/regular/history?${params}`);
      if (!res.ok) {
        setFlatRecords([]);
        return;
      }
      const json = await res.json();
      const docs = Array.isArray(json.data) ? json.data : [];
      const flat = [];
      docs.forEach(doc => {
        const dateIST = doc.date ? new Date(new Date(doc.date).getTime() + 5.5 * 3600000).toISOString().slice(0, 10) : null;
        (doc.entries || []).forEach(entry => {
          flat.push({
            studentId: String(entry.studentId),
            status: entry.status,
            date: dateIST,
            day: doc.day,
            periodId: doc.periodId,
            periodLabel: doc.periodLabel,
            subject: doc.subject || null,
            teacher: doc.teacher || null,
            timeRange: doc.timeRange || null
          });
        });
      });
      setFlatRecords(flat);
    } catch (e) {
      setAttError(e.message);
      setFlatRecords([]);
    } finally {
      setLoadingAtt(false);
    }
  }, [dbClassName, section, group, viewMode, singleDate, weekStart, month, year, term]);
  useEffect(() => {
    fetchAttendance();
  }, [fetchAttendance]);

  // Student summary
  const summary = useMemo(() => students.map(stu => {
    const id = String(stu._id);
    const recs = flatRecords.filter(r => r.studentId === id);
    const present = recs.filter(r => r.status === "P").length;
    const absent = recs.filter(r => r.status === "A").length;
    const late = recs.filter(r => r.status === "L").length;
    const total = recs.length;
    return {
      ...stu,
      present,
      absent,
      late,
      total,
      perc: pct(present + late, total)
    };
  }), [students, flatRecords]);

  // Aggregates
  const agg = useMemo(() => {
    const days = [...new Set(flatRecords.map(r => r.date))].length;
    const present = summary.reduce((s, r) => s + r.present, 0);
    const absent = summary.reduce((s, r) => s + r.absent, 0);
    const late = summary.reduce((s, r) => s + r.late, 0);
    const avgAtt = flatRecords.length > 0 ? pct(present + late, flatRecords.length) : 0;
    return {
      days,
      present,
      absent,
      late,
      avgAtt
    };
  }, [flatRecords, summary]);

  // Filter + sort
  const displayed = useMemo(() => summary.filter(s => !search || s.studentName?.toLowerCase().includes(search.toLowerCase()) || String(s.rollNo).includes(search)).sort((a, b) => {
    if (sortBy === "name") return (a.studentName || "").localeCompare(b.studentName || "");
    if (sortBy === "pct") return b.perc - a.perc;
    if (sortBy === "pct_asc") return a.perc - b.perc;
    return (a.rollNo || 0) - (b.rollNo || 0);
  }), [summary, search, sortBy]);
  const periodLabel = useMemo(() => {
    if (viewMode === "single") return singleDate;
    if (viewMode === "week") return `${weekStart} – ${addDays(weekStart, 6)}`;
    if (viewMode === "month") return fmtMonth(month);
    if (viewMode === "year") return year;
    if (viewMode === "term") return `${term} · ${year}`;
    return "";
  }, [viewMode, singleDate, weekStart, month, year, term]);
  const TABS = [{
    key: "single",
    label: "Single Date"
  }, {
    key: "week",
    label: "Week"
  }, {
    key: "month",
    label: "Month"
  }, {
    key: "year",
    label: "Year"
  }, {
    key: "term",
    label: "Term"
  }];
  return <div style={{
    color: "#1e293b",
    fontFamily: "'Poppins', sans-serif",
    background: "#fff",
    borderRadius: 24,
    padding: "24px 28px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  }}>
      {/* Title */}
      <div style={{
      marginBottom: 20
    }}>
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        marginBottom: 4
      }}>
          <i className="fas fa-chart-line" style={{
          fontSize: 22,
          color: "#3b82f6"
        }}></i>
          <span style={{
          fontWeight: 700,
          fontSize: 22,
          color: "#0f172a"
        }}>Regular Attendance</span>
        </div>
        <div style={{
        fontSize: 13,
        color: "#64748b"
      }}>
          {className}{group ? ` · ${group}` : ""} — Section {section}
          &nbsp;·&nbsp;
          <span style={{
          color: loadingAtt ? "#f59e0b" : flatRecords.length > 0 ? "#10b981" : "#64748b"
        }}>
            {loadingAtt ? <i className="fas fa-spinner fa-pulse"></i> : `${flatRecords.length} record${flatRecords.length !== 1 ? "s" : ""} in range`}
          </span>
          &nbsp;·&nbsp;<span style={{
          color: "#3b82f6"
        }}><i className="far fa-calendar-alt"></i> {periodLabel}</span>
        </div>
      </div>

      {/* Subject chips */}
      {timetableSubjects.length > 0 && <div style={{
      marginBottom: 20
    }}>
          <div style={{
        fontSize: 11,
        color: "#64748b",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: 8
      }}>
            Subjects in this class
          </div>
          <div style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap"
      }}>
            {timetableSubjects.map((s, i) => {
          const c = subjColor(i);
          return <div key={s.subject} style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            background: "#fff",
            border: `1px solid ${c.border}`,
            borderRadius: 30,
            padding: "6px 14px"
          }}>
                  <span style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: c.dot
            }} />
                  <span style={{
              fontSize: 13,
              fontWeight: 600,
              color: c.text
            }}>{s.subject}</span>
                  {s.teacher && <span style={{
              fontSize: 12,
              color: "#64748b"
            }}>· {s.teacher}</span>}
                </div>;
        })}
          </div>
        </div>}

      {/* View tabs */}
      <div style={{
      display: "flex",
      gap: 8,
      marginBottom: 20,
      flexWrap: "wrap"
    }}>
        {TABS.map(({
        key,
        label
      }) => <button key={key} onClick={() => setViewMode(key)} style={{
        padding: "8px 20px",
        borderRadius: 40,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "'Poppins', sans-serif",
        transition: "all .15s",
        border: viewMode === key ? "none" : "1px solid #e2e8f0",
        background: viewMode === key ? "#3b82f6" : "#fff",
        color: viewMode === key ? "#fff" : "#475569",
        boxShadow: viewMode === key ? "0 2px 8px rgba(59,130,246,0.2)" : "none"
      }}>
            {label}
          </button>)}
      </div>

      {/* Date picker */}
      <div style={{
      marginBottom: 24,
      display: "flex",
      alignItems: "center",
      gap: 16,
      flexWrap: "wrap"
    }}>
        {viewMode === "single" && <><label style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 500
        }}><i className="far fa-calendar-alt"></i> Date:</label><input type="date" value={singleDate} onChange={e => setSingleDate(e.target.value)} style={PS} /></>}
        {viewMode === "week" && <><label style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 500
        }}><i className="fas fa-calendar-week"></i> Week of:</label><input type="date" value={weekStart} onChange={e => setWeekStart(getMondayOf(e.target.value))} style={PS} /><span style={{
          fontSize: 13,
          color: "#64748b"
        }}><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /> {addDays(weekStart, 6)}</span></>}
        {viewMode === "month" && <><label style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 500
        }}><i className="far fa-calendar-alt"></i> Month:</label><input type="month" value={month} onChange={e => setMonth(e.target.value)} style={PS} /></>}
        {viewMode === "year" && <><label style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 500
        }}><i className="far fa-calendar-alt"></i> Year:</label><select value={year} onChange={e => setYear(e.target.value)} style={PS}>{[2023, 2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}</select></>}
        {viewMode === "term" && <><label style={{
          fontSize: 13,
          color: "#475569",
          fontWeight: 500
        }}><i className="fas fa-layer-group"></i> Term:</label><select value={term} onChange={e => setTerm(e.target.value)} style={PS}><option value="T1">Term 1 (Jun–Sep)</option><option value="T2">Term 2 (Oct–Jan)</option><option value="T3">Term 3 (Feb–May)</option></select><select value={year} onChange={e => setYear(e.target.value)} style={PS}>{[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}</select></>}
        <button onClick={() => {
        fetchStudents();
        fetchAttendance();
      }} style={{
        padding: "8px 20px",
        borderRadius: 40,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "'Poppins', sans-serif",
        background: "#eff6ff",
        border: "1px solid #bfdbfe",
        color: "#2563eb",
        marginLeft: "auto"
      }}><i className="fas fa-sync-alt"></i> Refresh</button>
      </div>

      {/* Stats cards */}
      <div style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
      gap: 16,
      marginBottom: 24
    }}>
        {[{
        icon: "fas fa-calendar-day",
        val: agg.days,
        label: "Working Days",
        color: "#3b82f6"
      }, {
        icon: "fas fa-clipboard-list",
        val: flatRecords.length,
        label: "Total Records",
        color: "#3b82f6"
      }, {
        icon: "fas fa-check-circle",
        val: agg.present,
        label: "Present",
        color: "#10b981"
      }, {
        icon: "fas fa-times-circle",
        val: agg.absent,
        label: "Absent",
        color: "#ef4444"
      }, {
        icon: "fas fa-clock",
        val: agg.late,
        label: "Late",
        color: "#f59e0b"
      }, {
        icon: "fas fa-chart-simple",
        val: `${agg.avgAtt}%`,
        label: "Avg Attendance",
        color: agg.avgAtt >= 75 ? "#10b981" : agg.avgAtt > 0 ? "#f59e0b" : "#ef4444"
      }].map(({
        icon,
        val,
        label,
        color
      }) => <div key={label} style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 20,
        padding: "16px 12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
      }}>
            <i className={icon} style={{
          fontSize: 20,
          color,
          marginBottom: 8
        }}></i>
            <div style={{
          fontSize: 26,
          fontWeight: 700,
          color
        }}>{val}</div>
            <div style={{
          fontSize: 11,
          color: "#64748b",
          marginTop: 4
        }}>{label}</div>
          </div>)}
      </div>

      {/* Search + sort */}
      <div style={{
      display: "flex",
      gap: 12,
      marginBottom: 20,
      alignItems: "center",
      flexWrap: "wrap"
    }}>
        <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 40,
        padding: "8px 18px",
        minWidth: 220
      }}>
          <i className="fas fa-search" style={{
          color: "#94a3b8"
        }}></i>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search student name or roll no…" style={{
          background: "none",
          border: "none",
          outline: "none",
          color: "#1e293b",
          fontSize: 13,
          flex: 1
        }} />
          {search && <span onClick={() => setSearch("")} style={{
          cursor: "pointer",
          color: "#94a3b8",
          fontSize: 12
        }}><i className="fas fa-times"></i></span>}
        </div>
        <span style={{
        fontSize: 12,
        color: "#64748b"
      }}>Sort by:</span>
        {[{
        key: "roll",
        label: "Roll #"
      }, {
        key: "name",
        label: "Name"
      }, {
        key: "pct",
        label: "%"
      }, {
        key: "pct_asc",
        label: "%"
      }].map(({
        key,
        label
      }) => <button key={key} onClick={() => setSortBy(key)} style={{
        padding: "6px 16px",
        borderRadius: 40,
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "'Poppins', sans-serif",
        transition: "all .15s",
        background: sortBy === key ? "#3b82f6" : "#fff",
        color: sortBy === key ? "#fff" : "#475569",
        border: sortBy === key ? "none" : "1px solid #e2e8f0"
      }}>{label}</button>)}
      </div>

      {/* Errors */}
      {(stuError || attError) && <div style={{
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: 16,
      padding: "12px 20px",
      color: "#dc2626",
      fontSize: 13,
      marginBottom: 16
    }}>
          <i className="fas fa-exclamation-triangle"></i> {stuError || attError}
        </div>}

      {/* Loading */}
      {loadingStu && <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 20,
      padding: "60px 0",
      textAlign: "center",
      color: "#94a3b8"
    }}>
          <i className="fas fa-spinner fa-pulse"></i> Loading students…
        </div>}

      {/* No students */}
      {!loadingStu && students.length === 0 && !stuError && <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 20,
      padding: "60px 20px",
      textAlign: "center"
    }}>
          <i className="fas fa-user-graduate" style={{
        fontSize: 48,
        color: "#cbd5e1",
        marginBottom: 16
      }}></i>
          <div style={{
        fontWeight: 700,
        fontSize: 18,
        color: "#0f172a",
        marginBottom: 8
      }}>No Students Found</div>
          <div style={{
        fontSize: 14,
        color: "#64748b"
      }}>No admitted students for {className} – Section {section}.</div>
        </div>}

      {/* Student table */}
      {!loadingStu && students.length > 0 && <div style={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderRadius: 20,
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
    }}>
          {loadingAtt && <div style={{
        padding: "12px 24px",
        background: "#eff6ff",
        borderBottom: "1px solid #bfdbfe",
        fontSize: 13,
        color: "#2563eb"
      }}>
              <i className="fas fa-spinner fa-pulse"></i> Fetching attendance for {periodLabel}…
            </div>}
          {!loadingAtt && flatRecords.length === 0 && !attError && <div style={{
        padding: "12px 24px",
        background: "#fffbeb",
        borderBottom: "1px solid #fde68a",
        fontSize: 13,
        color: "#b45309"
      }}>
              <i className="fas fa-info-circle"></i> No attendance records for {periodLabel}. Mark attendance in the LogBook page first.
            </div>}

          <div style={{
        overflowX: "auto"
      }}>
            <table style={{
          width: "100%",
          borderCollapse: "collapse",
          minWidth: 700
        }}>
              <thead>
                <tr style={{
              background: "#fff",
              borderBottom: "2px solid #e2e8f0"
            }}>
                  {["#", "Roll", "Student Name", "Present", "Absent", "Late", "Periods", "Attendance %", ""].map((h, i) => <th key={i} style={{
                padding: "14px 16px",
                textAlign: i === 8 ? "center" : "left",
                fontSize: 11,
                fontWeight: 600,
                color: "#475569",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {displayed.map((s, idx) => {
              const color = attColor(s.perc);
              return <tr key={String(s._id)} style={{
                borderBottom: "1px solid #f1f5f9",
                transition: "background .12s"
              }} onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{
                  padding: "14px 16px",
                  fontSize: 13,
                  color: "#64748b"
                }}>{idx + 1}</td>
                      <td style={{
                  padding: "14px 16px"
                }}>
                        <span style={{
                    background: "#eef2ff",
                    color: "#3b82f6",
                    borderRadius: 8,
                    padding: "3px 10px",
                    fontSize: 12,
                    fontWeight: 600
                  }}>{s.rollNo}</span>
                      </td>
                      <td style={{
                  padding: "14px 16px"
                }}>
                        <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10
                  }}>
                          <div style={{
                      width: 34,
                      height: 34,
                      borderRadius: 12,
                      background: "#eef2ff",
                      color: "#3b82f6",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      fontSize: 14,
                      flexShrink: 0
                    }}>
                            <i className="fas fa-user"></i>
                          </div>
                          <div>
                            <div style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#0f172a"
                      }}>{s.studentName}</div>
                            <div style={{
                        fontSize: 11,
                        color: "#64748b"
                      }}>{s.gender || ""}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{
                  padding: "14px 16px"
                }}><span style={{
                    color: "#10b981",
                    fontWeight: 700,
                    fontSize: 15
                  }}>{s.present}</span></td>
                      <td style={{
                  padding: "14px 16px"
                }}><span style={{
                    color: "#ef4444",
                    fontWeight: 700,
                    fontSize: 15
                  }}>{s.absent}</span></td>
                      <td style={{
                  padding: "14px 16px"
                }}><span style={{
                    color: "#f59e0b",
                    fontWeight: 700,
                    fontSize: 15
                  }}>{s.late}</span></td>
                      <td style={{
                  padding: "14px 16px"
                }}><span style={{
                    color: "#64748b",
                    fontSize: 13
                  }}>{s.total}</span></td>
                      <td style={{
                  padding: "14px 16px",
                  minWidth: 130
                }}>
                        <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6
                  }}>
                          <span style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: s.total > 0 ? color : "#cbd5e1",
                      minWidth: 40
                    }}>{s.total > 0 ? `${s.perc}%` : "—"}</span>
                          {s.total > 0 && s.perc < 75 && <span style={{
                      fontSize: 10,
                      color: "#ef4444",
                      fontWeight: 600,
                      background: "#fef2f2",
                      padding: "2px 8px",
                      borderRadius: 20
                    }}>LOW</span>}
                        </div>
                        <Bar value={s.perc} color={s.total > 0 ? color : "#e2e8f0"} />
                      </td>
                      <td style={{
                  padding: "14px 16px",
                  textAlign: "center"
                }}>
                        <button onClick={() => setModalStu(s)} style={{
                    background: "#eef2ff",
                    border: "none",
                    borderRadius: 30,
                    color: "#3b82f6",
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "6px 16px",
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                    whiteSpace: "nowrap"
                  }}>
                          <i className="fas fa-eye"></i> View
                        </button>
                      </td>
                    </tr>;
            })}
                {displayed.length === 0 && <tr>
                    <td colSpan={9} style={{
                padding: "48px 0",
                textAlign: "center",
                color: "#94a3b8",
                fontSize: 14
              }}>
                      <i className="fas fa-user-slash"></i> No students match "{search}"
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Footer */}
      {!loadingStu && students.length > 0 && <div style={{
      marginTop: 16,
      display: "flex",
      justifyContent: "space-between",
      fontSize: 12,
      color: "#64748b",
      flexWrap: "wrap",
      gap: 8
    }}>
          <span>
            Showing {displayed.length} of {students.length} students
            {search ? ` · filtered by "${search}"` : ""} · {periodLabel}
          </span>
          <span>
            {flatRecords.length > 0 ? `${agg.avgAtt}% class average` : "No attendance data for this period"}
          </span>
        </div>}

      {/* Modal */}
      {modalStu && <StudentModal student={modalStu} flatRecords={flatRecords} timetableSubjects={timetableSubjects} onClose={() => setModalStu(null)} />}
    </div>;
}